import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { objectSort } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import useStaffList from '../../Hooks/useStaffList';
import { getCurrentUser } from '@vantiva-sa/homesight_humaui/Store/authStore';
import { Actions } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import useContacts from '../../Hooks/useContacts';
import { useInviteContactsToUser } from '@/Components/Hooks/useInviteContactsToUser';

const RequiredFields = {};
const DefaultValues = {
  sourcerelationship: '',
  targetrelationship: '',
};

export const useAssignStaffHandler = (record, onActionClick, openSubMenu, options = {}) => {
  const { triggerApi = true } = options;
  const [isFirstModalOpen, setIsFirstModalOpen] = useState(false);
  const [isSecondModalOpen, setIsSecondModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [role, setRole] = useState('');
  const [formData, setFormData] = useState(DefaultValues);
  const [isValid, setIsValid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const trans = useSelector(translator);
  const currentUser = useSelector(getCurrentUser);
  const [searchNameText, setSearchNameText] = useState(null);
  const [searchNameApplied, setSearchNameApplied] = useState(false);
  const inviteContactsToUser = useInviteContactsToUser({ userHubsUuid: currentUser?.useruuid });

  const hubUseruuid = record?.useruuid;
  const {
    data: accountContacts,
    isFetching: accountfetchingContacts,
    refetch: accountfetchContacts,
  } = useContacts(
    {
      useruuid: hubUseruuid,
    },
    { enabled: triggerApi ? true : false }
  );

  // Fetch staff list
  const { data: staffList, isFetching: fetchingStaffList } = useStaffList({
    useruuid: currentUser?.useruuid,
  });

  const remainStaffList = useMemo(() => staffList ?? [], [staffList]);
  const mOrderedStaffList = useMemo(
    () => [...remainStaffList].sort(objectSort('firstname')),
    [remainStaffList]
  );

  useEffect(() => {
    validate(false);
  }, [selectedStaff, formData]);

  const handleAddStaff = (staff) => {
    setSelectedStaff(staff);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData({ ...formData, [name]: value });
  };

  const validate = (showError = true) => {
    let valid = true;
    let error = '';
    let erroredFields = [];

    if (!selectedStaff) {
      error = trans('PLEASESELECTASTAFFMEMBERTOASSIGN');
      valid = false;
    }
    for (let field of Object.keys(RequiredFields)) {
      if (!formData[field]) {
        erroredFields.push(RequiredFields[field]);
        valid = false;
      }
    }
    if (erroredFields.length) {
      error += ` ${trans('PLEASEFILLREQUIREDFIELDS')} ${erroredFields.join(', ')}.`;
    }
    if (showError && !valid) {
      // message.error(error, 10);
      console.error(error, 10);
    }
    setIsValid(valid);
    return valid;
  };
  const handleCancel = () => {
    setSelectedStaff(null);
    setFormData(DefaultValues);
    onActionClick(null, null, true)();
  };

  const handleSubmit = () => {
    if (!validate()) return;

    setProcessing(true);
    const userObj = {
      ...formData,
      email: selectedStaff.email,
      rolename: role,
    };

    const successCallback = () => {
      setProcessing(false);
      if (openSubMenu !== true) {
        onActionClick(null, null, true)();
      } else {
        onActionClick(Actions.hubDetails, record, true, true)();
      }
    };

    const errorCallback = (error) => {
      setProcessing(false);
      console.error('Error assigning staff', error);
    };

    inviteContactsToUser.mutate({
      userObj,
      apiCallback,
      trans,
      successCallback,
      errorCallback,
      userUuid: record?.useruuid,
    });
  };

  const handleAssignStaff = () => {
    handleSubmit();
    setIsSecondModalOpen(false); // second modal closes
    handleCloseFirstModal(); // first modal closes
  };

  const handleOpenSecondModal = () => {
    setIsSecondModalOpen(true);
    setRole('');
    setIsFirstModalOpen(false);
  };
  const handleCloseSecondModal = () => {
    setFormData(DefaultValues);
    setRole('');
    setIsSecondModalOpen(false);
    setIsFirstModalOpen(false);
  };

  const handleRoleSelection = useCallback((role) => {
    setRole(role);
  }, []);

  const openAssignStaffModal = () => {
    setSelectedStaff(null);
    setRole('');
    setIsFirstModalOpen(true);
  };
  const handleCloseFirstModal = () => {
    setIsFirstModalOpen(false);
    setSelectedStaff(null);
    setSearchNameApplied(false);
    setSearchNameText(null);
  };

  return {
    isFirstModalOpen,
    openAssignStaffModal,
    handleCloseFirstModal,
    selectedStaff,
    handleAddStaff,
    mOrderedStaffList,
    fetchingStaffList,
    role,
    handleRoleSelection,
    handleInputChange,
    formData,
    handleAssignStaff,
    handleOpenSecondModal,
    handleCloseSecondModal,
    processing,
    isValid,
    handleCancel,
    isSecondModalOpen,
    setSelectedStaff,
    setSearchNameApplied,
    searchNameApplied,
    setSearchNameText,
    searchNameText,
    accountContacts,
    accountfetchingContacts,
    accountfetchContacts,
  };
};
